// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the WS8a density presets (foundations-first reorder).
// A density preset is a single control that trades chrome for canvas: Guide is
// spacious and guided, Focus collapses the chrome toward the edges so the Model
// dominates, Support sits between. It resolves to the *base* zone sizes and a few
// content-verbosity flags; the zone frame (computeZoneLayout) then tiles those
// sizes, so density only ever changes what's shown IN a zone or how big it is --
// never where a zone sits (acceptance criterion C3), and the Model is never
// covered (the frame clamps every size against MIN_MODEL_*).
//
// Interaction model: switching preset re-applies these base sizes; the user's
// drag-resize (Activities width / Results height) then fine-tunes from the
// preset baseline until the next switch. sliverHeight is preset-fixed (the top
// sliver is not user-resizable per the spec).
//
// The full per-zone × per-preset intent from the acceptance spec:
//   - Top sliver: Guide full / Support tighter / Focus minimal (mode only).
//   - Left Tools: Guide pinned+labeled / Support icons+short / Focus edge-rail.
//   - Center Model: Guide present / Support larger / Focus dominant (derived --
//     the Model absorbs whatever the other zones give back).
//   - Right Activities: Guide pinned+list+helper / Support list, no helper /
//     Focus thin/collapsed.
//   - Bottom Results: Guide visible+chart / Support peek strip / Focus a handle.
// Tools sizing is carried as a mode flag (the Tools zone is a later workstream;
// toolsWidth stays 0 here so it doesn't reserve space before it renders).
// MVP Focus = docked-but-collapsed; true summon/hover-peek is a fast-follow, so
// Focus keeps the rail/band docked at a minimal-but-usable size rather than
// hiding them behind a summon affordance that doesn't exist yet.

export type DensityPreset = 'guide' | 'support' | 'focus';

/** How the (future) Left Tools rail presents at each density. Carried now so the
 *  preset table is complete; consumed when the Tools zone is built. */
export type ToolsMode = 'labeled' | 'compact' | 'rail';

export interface DensityConfig {
  /** Fixed top-sliver height for this preset (not user-resizable). */
  readonly sliverHeight: number;
  /** Base Left Tools width. 0 until the Tools zone workstream lands. */
  readonly toolsWidth: number;
  /** Base Right Activities width (the user can drag from here). */
  readonly activitiesWidth: number;
  /** Base Bottom Results height when a result exists (the user can drag from here). */
  readonly resultsHeight: number;
  /** Tools presentation intent (for the future Tools zone). */
  readonly toolsMode: ToolsMode;
  /** Show only the mode switch in the sliver (Focus), hiding search/breadcrumb extras. */
  readonly sliverMinimal: boolean;
  /** Show the Activities rail's helper/explanatory text (Guide only). */
  readonly activitiesHelper: boolean;
}

export const DENSITY_PRESETS: readonly DensityPreset[] = ['guide', 'support', 'focus'];

/** WS8a acceptance C1: the editor opens in Guide. */
export const DEFAULT_DENSITY: DensityPreset = 'guide';

// Base sizes per preset. These are principles-not-pixels (tightened at the visual
// pass) and are intentionally a gradient: each dimension shrinks Guide→Focus so
// switching presets visibly reflows (criterion C2) while the Model grows. Every
// size stays within what the frame will accept; the frame still clamps, so these
// can never collapse or cover the Model on their own.
const CONFIGS: Readonly<Record<DensityPreset, DensityConfig>> = {
  guide: {
    sliverHeight: 56,
    toolsWidth: 0,
    activitiesWidth: 360,
    resultsHeight: 220,
    toolsMode: 'labeled',
    sliverMinimal: false,
    activitiesHelper: true,
  },
  support: {
    sliverHeight: 48,
    toolsWidth: 0,
    activitiesWidth: 300,
    resultsHeight: 150,
    toolsMode: 'compact',
    sliverMinimal: false,
    activitiesHelper: false,
  },
  focus: {
    sliverHeight: 40,
    toolsWidth: 0,
    activitiesWidth: 256,
    resultsHeight: 104,
    toolsMode: 'rail',
    sliverMinimal: true,
    activitiesHelper: false,
  },
};

/** Resolve a density preset to its base zone sizes + content-verbosity flags. */
export function densityConfig(preset: DensityPreset): DensityConfig {
  return CONFIGS[preset] ?? CONFIGS[DEFAULT_DENSITY];
}

/** The next preset in the Guide→Support→Focus→Guide cycle (for a click-to-cycle
 *  control or keyboard shortcut). */
export function nextDensity(preset: DensityPreset): DensityPreset {
  const i = DENSITY_PRESETS.indexOf(preset);
  return DENSITY_PRESETS[(i + 1) % DENSITY_PRESETS.length];
}

/** Human-readable label for a preset (UI + a11y). */
export function densityLabel(preset: DensityPreset): string {
  switch (preset) {
    case 'guide':
      return 'Guide';
    case 'support':
      return 'Support';
    case 'focus':
      return 'Focus';
  }
}
